import { annotate } from "rough-notation";

import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useInView, motion } from "framer-motion";

import TextType from "./typingOut";
import { Button } from "@heroui/react";
import { Sparkles, Pen, TextCursor } from 'lucide-react';


export const FirstTimeLetter = ({ onContinueClick }) => {
  const [hideCursor, setHideCursor] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  const [visible, setVisible] = useState(false);

  const [mounted, setMounted] = useState(false);


  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200); // delay
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  const closePopup = () => {
    setVisible(false);
    const t = setTimeout(() => onContinueClick(), 400); // delay
    return () => clearTimeout(t);
  }

  // for auto-scroll 
  const scrollContainerRef = useRef(null);
  const typingEndRef = useRef(null);

  if (!mounted) return null;

  return createPortal(
    <div className={`fixed inset-0 
                        bg-black/40 
                        backdrop-blur-sm 
                        z-[49] 
                        flex items-center justify-center
                        transition-opacity duration-300 ease-out
                        ${visible ? 'opacity-100' : 'opacity-0'}
        `}
    >

      <div className={`
    bg-white/65 rounded-3xl
    p-5 lg:p-9 lg:pt-6
    min-w-[95vw] max-w-[95vw]
    lg:min-w-[80vw] lg:max-w-[80vw]
    max-h-[95vh] lg:h-max
    relative overflow-hidden

    transition-all duration-400 ease-out
    ${visible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-5 scale-95'
        }
  `}>

        <div
          className="
      absolute inset-0
      bg-cover
      opacity-20
      pointer-events-none
    "
          style={{ backgroundImage: "url('/paper.jpg')" }}
        />

        <div className={`
          p-3 lg:pl-9 lg:pt-6 lg:pr-6 
          flex 
          justify-between relative`}>
          <RoughUnderline
            initialDelay={500}
            className="text-lg font-normal opacity-100 text-base-text"
            action="highlight"
          >
            英語の世界へ、ようこそ
          </RoughUnderline>

          <LogoStamp />
        </div>


        <div
          className="relative z-10 pl-6 py-5 lg:p-9 lg:pb-3 overflow-y-auto max-h-[65vh] lg:max-h-max overflow-y-auto overflow-x-hidden"
          ref={scrollContainerRef}
        >

          <RoughUnderline
            initialDelay={2800}
            action="bracket"
            iterations={2}
            padding={[0, 0, 0, 10]}
            multiline={false}
            brackets={["left"]}
          >
            <pre className="text-transparent whitespace-pre-wrap break-normal overflow-wrap-break-word py-2 max-w-full overflow-x-hidden">
              {`親愛なる学習者へ

言葉を学ぶということは、新しい色で世界を塗り直すようなものです。見慣れた景色が、まるで違って見えてくる。私、アオメが、その静かな変化に寄り添わせてください。

初めての方は、まず文法ページで英語という言語の骨格に触れてみてください。そしてスターターデッキで、最初の言葉たちと出会いましょう。それらは、やがてあなたを支える根となります。

準備が整ったら、扉は開かれます。あなたのペースで、英語の世界を歩み始めてください。

日々、テキストや動画から言葉と出会うとき、欲張らないでください。今、本当に自分のものにしたいと感じる言葉だけを、丁寧に選んでパーソナルデッキに加えていくのです。少しずつ、確実に。

ダッシュボードには、あなたのレベルに合わせた日々のタスクが待っています。アオメの機能を最大限に活かすための道標として、ぜひ確認してください。

もし、複雑な文法や構造に出会い、謎が解けないときは——私に尋ねてください。あなたの好奇心に、いつでも応えます。

そして、決して忘れないでください——言語は、たくさん浴びること、聴くことから始まります。その後に芽生える好奇心と、自ら声に出す勇気。この二つが重なるとき、言葉は本当にあなたのものになるのです。

小さなヒント:すでに英語に触れたことのある方は、スターターデッキを一度確認し、よく知っている言葉は「非表示」にしておきましょう。あなたの旅は、もう少し先から始まるのですから。

さあ、ゆっくりと、しかし確かな足取りで。 
あなたの学びを、心から応援しています。

アオメより`}
            </pre>
          </RoughUnderline>

          <div className="absolute inset-0 pl-6 py-7 lg:px-9 lg:py-11 pointer-events-none max-w-max">
            <TextType
              initialDelay={1600}
              typingSpeed={100}
              cursorCharacter={"__"}
              className="text-base-text whitespace-pre-wrap break-normal overflow-wrap-break-word max-w-full overflow-x-hidden"
              text={"親愛なる学習者へ"}
            />
            <TextType
              initialDelay={2800}
              variableSpeed={{ min: 15, max: 45 }}
              cursorCharacter={"__"}
              hideCursorWhileTyping={hideCursor}
              setHideCursor={setHideCursor}
              onComplete={() => setIsFinished(true)}

              onChar={() => {
                const container = scrollContainerRef.current;
                const end = typingEndRef.current;
                if (!container || !end) return;

                container.scrollTop =
                  end.offsetTop - container.clientHeight + 40;
              }}

              className="text-base-text whitespace-pre-wrap break-normal overflow-wrap-break-word max-w-full overflow-x-hidden"
              text={`
言葉を学ぶということは、新しい色で世界を塗り直すようなものです。見慣れた景色が、まるで違って見えてくる。私、アオメが、その静かな変化に寄り添わせてください。

初めての方は、まず文法ページで英語という言語の骨格に触れてみてください。そしてスターターデッキで、最初の言葉たちと出会いましょう。それらは、やがてあなたを支える根となります。

準備が整ったら、扉は開かれます。あなたのペースで、英語の世界を歩み始めてください。

日々、テキストや動画から言葉と出会うとき、欲張らないでください。今、本当に自分のものにしたいと感じる言葉だけを、丁寧に選んでパーソナルデッキに加えていくのです。少しずつ、確実に。

ダッシュボードには、あなたのレベルに合わせた日々のタスクが待っています。アオメの機能を最大限に活かすための道標として、ぜひ確認してください。

もし、複雑な文法や構造に出会い、謎が解けないときは—私に尋ねてください。あなたの好奇心に、いつでも応えます。

そして、決して忘れないでください—言語は、たくさん浴びること、聴くことから始まります。その後に芽生える好奇心と、自ら声に出す勇気。この二つが重なるとき、言葉は本当にあなたのものになるのです。

小さなヒント: すでに英語に触れたことのある方は、スターターデッキを一度確認し、よく知っている言葉は「非表示」にしておきましょう。あなたの旅は、もう少し先から始まるのですから。

さあ、ゆっくりと、しかし確かな足取りで。 
あなたの学びを、心から応援しています。

アオメより`} />

            <div ref={typingEndRef} />

          </div>


        </div>

        <div className="pt-3 flex justify-end">
          <Button
            className={`bg-button text-white font-light ${isFinished ? "opacity-1" : "opacity-0"} transition-opacity duration-500 ease-out`}
            startContent={<Sparkles strokeWidth={1} className="text-white size-5" />}
            isDisabled={!isFinished}
            onPress={closePopup}
          >
            学びを始める
          </Button>
        </div>
      </div>

    </div>, document.body
  )

}



const LogoStamp = () => {
  return (

    <svg height="20" viewBox="0 0 382 205" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4B4A49]">
      <path d="M107.975 204.636C94.8473 206.648 82.3005 197.48 83.2613 184.153C83.7971 176.722 85.0955 169.344 87.1489 162.141C91.4208 147.155 98.8597 133.274 108.957 121.448C119.053 109.623 131.57 100.132 145.65 93.6246C159.729 87.1174 175.04 83.7477 190.534 83.746C206.027 83.7443 221.338 87.1107 235.42 93.6148C249.501 100.119 262.019 109.607 272.119 121.431C282.218 133.254 289.66 147.133 293.935 162.118C295.99 169.321 297.29 176.698 297.827 184.129C298.791 197.456 286.246 206.627 273.118 204.618C259.99 202.61 251.453 189.918 248.124 176.983C247.994 176.477 247.857 175.973 247.714 175.47C245.35 167.184 241.235 159.51 235.651 152.972C230.066 146.435 223.144 141.188 215.358 137.592C207.572 133.995 199.106 132.134 190.539 132.135C181.972 132.136 173.506 133.999 165.72 137.597C157.935 141.195 151.014 146.443 145.431 152.982C139.848 159.521 135.735 167.196 133.373 175.483C133.229 175.985 133.093 176.49 132.963 176.995C129.637 189.932 121.103 202.625 107.975 204.636Z" fill="currentColor" />
      <path d="M0 183.944C0 172.33 9.35658 162.914 20.8985 162.914H41.797C53.3389 162.914 62.6955 172.33 62.6955 183.944C62.6955 195.558 53.3389 204.973 41.797 204.973H20.8985C9.35657 204.973 0 195.558 0 183.944Z" fill="currentColor" />
      <path d="M318.395 183.943C318.395 172.329 327.751 162.914 339.293 162.914H360.192C371.734 162.914 381.09 172.329 381.09 183.943C381.09 195.557 371.734 204.973 360.192 204.973H339.293C327.751 204.973 318.395 195.557 318.395 183.943Z" fill="currentColor" />
      <path d="M193.004 63.0877C181.462 63.0877 172.105 53.6726 172.105 42.0584V21.0292C172.105 9.4151 181.462 0 193.004 0C204.546 0 213.902 9.41511 213.902 21.0292V42.0585C213.902 53.6726 204.546 63.0877 193.004 63.0877Z" fill="currentColor" />
      <path d="M263.348 78.3865C253.352 72.5794 249.928 59.7181 255.699 49.66L266.148 31.4482C271.919 21.39 284.7 17.9439 294.696 23.7509C304.691 29.558 308.116 42.4193 302.345 52.4774L291.896 70.6892C286.125 80.7474 273.344 84.1935 263.348 78.3865Z" fill="currentColor" />
      <path d="M305.487 124.842C299.716 114.784 303.141 101.923 313.136 96.1157L331.235 85.6011C341.231 79.794 354.012 83.2402 359.783 93.2983C365.554 103.356 362.129 116.218 352.133 122.025L334.035 132.539C324.039 138.346 311.258 134.9 305.487 124.842Z" fill="currentColor" />
      <path d="M26.7993 93.2983C32.5702 83.2402 45.3515 79.794 55.3471 85.6011L73.4458 96.1157C83.4414 101.923 86.8661 114.784 81.0952 124.842C75.3242 134.9 62.5429 138.346 52.5473 132.539L34.4486 122.025C24.453 116.218 21.0283 103.356 26.7993 93.2983Z" fill="currentColor" />
      <path d="M91.2439 23.7507C101.24 17.9436 114.021 21.3898 119.792 31.4479L130.241 49.6598C136.012 59.7179 132.587 72.5792 122.592 78.3862C112.596 84.1933 99.8148 80.7471 94.0438 70.689L83.5946 52.4772C77.8236 42.419 81.2483 29.5578 91.2439 23.7507Z" fill="currentColor" />
    </svg>
  )
}

function useCombinedRefs(...refs) {
  const targetRef = React.useRef();

  React.useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
}

export const RoughUnderline = React.forwardRef(({
  children,
  action = "underline",
  color = "#A2AAC7",
  strokeWidth = 1.5,
  animationDuration = 600,
  iterations = 2,
  padding = 2,
  multiline = true,
  isView = false,
  brackets = "left",
  initialDelay = 0,
  onClick,
  isExiting = false,
  ...props
}, ref) => {

  const elementRef = useRef(null);
  const annotationRef = useRef(null);

  const combinedRef = useCombinedRefs(ref, elementRef);

  const isInView = useInView(elementRef, {
    once: true,
    margin: "-10%",
  });

  const shouldShow = !isView || isInView;
  const hasDrawn = useRef(false);
  useEffect(() => {
    if (!shouldShow || hasDrawn.current) return;

    const element = elementRef.current;
    if (!element) return;

    const annotationConfig = {
      type: action,
      color,
      strokeWidth,
      animationDuration,
      iterations,
      padding,
      multiline,
      brackets
    };

    const annotation = annotate(element, annotationConfig);
    annotationRef.current = annotation;

    const delay = setTimeout(() => {
      annotationRef.current?.show();
      hasDrawn.current = true;
    }, initialDelay);

    return () => {
      clearTimeout(delay);

      // if (element && annotationRef.current) {
      //   annotationRef.current.remove();
      // }
    };
  }, [
    shouldShow,
    action,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
    initialDelay,
  ]);

  useEffect(() => {
    if (isExiting && annotationRef.current) {
      annotationRef.current.hide()
    }
  }, [isExiting])

  return (
    <span
      // ref={elementRef} 
      className={`${action !== "highlight" ? "text-transparent" : "text-base-text"} break-keep whitespace-pre-wrap bg-transparent`}
      ref={combinedRef}
      {...props}
    >
      {children}
    </span>
  );
})